import { createRouter, createWebHistory } from "vue-router";

import { useAuth } from "@/stores/auth";
import { addBreadcrumb, captureException, isSentryAvailable } from "@/modules/sentry";
import authRoutes from "./modules/auth.js";

import MainLayout from "@/layout/main/MainLayout.vue";

const routes: any[] = [
  {
    path: "/",
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "/",
        name: "beneficiaries-list",
        meta: { title: "Beneficiaries List", requiresAuth: true, breadcrumb: ["Beneficiaries List"] },
        component: () =>
          import(
            /* webpackChunkName: "beneficiaries-list" */ "@/views/pages/BeneficiariesList.vue"
          ),
      },
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        meta: { title: "Not Found", requiresAuth: true, breadcrumb: ["404"] },
        component: () =>
          import(/* webpackChunkName: "system" */ "@/views/pages/NotFound.vue"),
      },
    ],
  },
  ...authRoutes,
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (isSentryAvailable()) {
    addBreadcrumb({
      category: "navigation",
      message: `Navigation: ${from.path} → ${to.path}`,
      level: "info",
      data: {
        from: from.path,
        to: to.path,
        toName: to.name != null ? String(to.name) : undefined,
        requiresAuth: to.meta.requiresAuth,
      },
    });
  }

  next();
});

router.onError((error) => {
  if (isSentryAvailable()) {
    captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        errorType: "router-error",
      },
      extra: {
        currentRoute: router.currentRoute.value.path,
      },
    });
  }
});

router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const authStore = useAuth();

  const isAuthenticated = authStore.isAuthenticated;

  if (requiresAuth && !isAuthenticated) {
    const restored = await authStore.checkAuth();

    if (restored) {
      next();

      return;
    }

    next("/auth/login");
  } else if (to.path === "/auth/login" && isAuthenticated) {
    next("/");
  } else {
    next();
  }
});

export default router;
